import {
  Button,
  ButtonGroup,
  ColSplit,
  elMt8,
  elRowGap4,
  FlexContainer,
  Grid,
  InputError,
  InputGroup,
} from '@reapit/elements'
import React from 'react'
import { GlobalError, useForm } from 'react-hook-form'
import { AnyObject, InferType, ObjectSchema } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { usePlatformUpdate } from '@reapitform/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { IAgentSettingsData } from '../../../../../api/agent-settings'
import { elMb4 } from '@reapitform/common-ui'
import { strictInput, yup } from '@reapitform/utils'

export interface IPaymentMethodsEditFormProps {
  onSuccess?: () => void
  onClose?: () => void
  onFailure?: () => void
  defaultData: IAgentSettingsData['paymentMethods'] & { [key: string]: any }
  queryKey?: string
}

const optionalObject = <ObjectType extends AnyObject | undefined>(
  requiredValue: string,
  schema: ObjectSchema<ObjectType>,
) => {
  return schema.when('paymentMethodSelected', {
    is: (value: string[]) => value.includes(requiredValue),
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional().nullable().default(undefined),
  })
}

const paymentMethodsValidationSchema = yup
  .object<IAgentSettingsData['paymentMethods'] & { paymentMethodSelected: string[] }>()
  .shape({
    paymentMethodSelected: yup
      .array(yup.string().required())
      .min(1, 'You must select at least one payment method.')
      .required('You must select at least one payment method.'),
    directDebit: optionalObject<Required<IAgentSettingsData['paymentMethods']['directDebit']> | undefined>(
      'directDebit',
      yup.object().shape({
        type: yup.string().required('Enter the debit type'),
      }),
    ),
    directCredit: optionalObject<Required<IAgentSettingsData['paymentMethods']['directCredit']> | undefined>(
      'directCredit',
      yup.object().shape({
        accountName: yup.string().required('Enter the account name'),
        bsb: yup.string().bsbNumber().required('Enter the bsb'),
        bankName: yup.string().required('Enter the bank name'),
        accountNumber: yup.string().bankAccountNumber().required('Enter the account number'),
      }),
    ),
    bpay: optionalObject<Required<IAgentSettingsData['paymentMethods']['bpay']> | undefined>(
      'bpay',
      yup.object().shape({
        billerCode: yup.string().bpayBillerCode().required('Enter the biller code'),
      }),
    ),
    cheque: optionalObject<Required<IAgentSettingsData['paymentMethods']['cheque']> | undefined>(
      'cheque',
      yup.object().shape({
        payableTo: yup.string().required('Enter the payable to'),
      }),
    ),
    cash: optionalObject<Required<IAgentSettingsData['paymentMethods']['cash']> | undefined>(
      'cash',
      yup.object().shape({
        placeOfPayment: yup.string().required('Enter the place of payment'),
      }),
    ),
    other: optionalObject<Required<IAgentSettingsData['paymentMethods']['other']> | undefined>(
      'other',
      yup.object().shape({
        type: yup.string().required('Enter the payment type').max(24, 'The type must not exceed 24 characters.'),
      }),
    ),
  })

type SchemaType = InferType<typeof paymentMethodsValidationSchema>

const PaymentMethodsEditForm: React.FC<IPaymentMethodsEditFormProps> = ({
  onClose,
  onSuccess,
  defaultData,
  queryKey,
}) => {
  const queryClient = useQueryClient()
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    unregister,
    formState: { errors },
  } = useForm<SchemaType>({
    defaultValues: {
      paymentMethodSelected: Object.keys(defaultData).map((paymentMethodKey) => paymentMethodKey),
      ...(defaultData as unknown as Omit<SchemaType, 'paymentMethodSelected'>),
    },
    resolver: yupResolver(paymentMethodsValidationSchema),
  })

  const [submitRequest, isSubmitLoading] = usePlatformUpdate<
    IAgentSettingsData['paymentMethods'],
    IAgentSettingsData['paymentMethods']
  >({
    path: '/api/Organization/PaymentMethods',
    method: 'PUT',
    headers: {},
    successMessage: 'Your payment methods successfully updated!',
    errorMessage: 'Oopps... Failed to update your payment methods!',
  })

  const handleFormSubmission = async (data: SchemaType) => {
    const payload: IAgentSettingsData['paymentMethods'] = {
      bpay: data.bpay,
      cash: data.cash,
      cheque: data.cheque,
      directCredit: data.directCredit,
      directDebit: data.directDebit,
      other: data.other,
    }

    const result = await submitRequest(payload)
    if (!result) return

    if (queryKey) {
      queryClient.setQueryData([queryKey], result)
      queryClient.invalidateQueries({ queryKey: [queryKey] })
    }
    onSuccess?.()
  }

  const paymentMethodSelected = watch('paymentMethodSelected')
  const directDebitSelected: boolean = paymentMethodSelected.includes('directDebit')
  const directCreditSelected: boolean = paymentMethodSelected.includes('directCredit')
  const bpaySelected: boolean = paymentMethodSelected.includes('bpay')
  const chequeSelected: boolean = paymentMethodSelected.includes('cheque')
  const cashSelected: boolean = paymentMethodSelected.includes('cash')
  const otherSelected: boolean = paymentMethodSelected.includes('other')

  const handleChangePaymentMethod = (name: keyof SchemaType, isChecked: boolean) => {
    if (!isChecked) return setValue('paymentMethodSelected', [...paymentMethodSelected, name])
    unregister(name)
    setValue(
      'paymentMethodSelected',
      paymentMethodSelected.filter((value) => value !== name),
    )
  }

  const emptyErrorMessage: string = errors.paymentMethodSelected?.message || ''

  return (
    <form onSubmit={handleSubmit(handleFormSubmission)}>
      <FlexContainer isFlexColumn className={elRowGap4}>
        <InputGroup
          hasError={Boolean(emptyErrorMessage)}
          inputAddOnText="Direct Debit"
          type="checkbox"
          checked={directDebitSelected}
          onChange={() => handleChangePaymentMethod('directDebit', directDebitSelected)}
        />
        {directDebitSelected && (
          <Grid className={elMb4}>
            <ColSplit>
              <InputGroup
                label="Type *"
                type="text"
                {...register('directDebit.type')}
                errorMessage={(errors.directDebit?.type as GlobalError)?.message}
                maxLength={200}
              />
            </ColSplit>
          </Grid>
        )}
        <InputGroup
          inputAddOnText="Direct Credit"
          type="checkbox"
          hasError={Boolean(emptyErrorMessage)}
          checked={directCreditSelected}
          onChange={() => handleChangePaymentMethod('directCredit', directCreditSelected)}
        />
        {directCreditSelected && (
          <Grid className={elMb4}>
            <ColSplit>
              <InputGroup
                label="Account Name *"
                type="text"
                {...register('directCredit.accountName')}
                errorMessage={errors.directCredit?.accountName?.message}
                maxLength={200}
              />
            </ColSplit>
            <ColSplit>
              <InputGroup
                label="Bank Name *"
                type="text"
                {...register('directCredit.bankName')}
                errorMessage={errors.directCredit?.bankName?.message}
                maxLength={200}
              />
            </ColSplit>
            <ColSplit>
              <InputGroup
                label="BSB  *"
                type="text"
                {...register('directCredit.bsb')}
                {...strictInput(/^[0-9-]*$/)}
                errorMessage={errors.directCredit?.bsb?.message}
              />
            </ColSplit>
            <ColSplit>
              <InputGroup
                label="Account Number *"
                type="text"
                {...register('directCredit.accountNumber')}
                {...strictInput(/^[0-9]*$/)}
                errorMessage={errors.directCredit?.accountNumber?.message}
              />
            </ColSplit>
          </Grid>
        )}
        <InputGroup
          inputAddOnText="BPAY"
          type="checkbox"
          hasError={Boolean(emptyErrorMessage)}
          checked={bpaySelected}
          onChange={() => handleChangePaymentMethod('bpay', bpaySelected)}
        />
        {bpaySelected && (
          <Grid className={elMb4}>
            <ColSplit>
              <InputGroup
                label="Biller Code *"
                type="text"
                {...register('bpay.billerCode')}
                {...strictInput(/^[0-9]*$/)}
                errorMessage={errors.bpay?.billerCode?.message}
              />
            </ColSplit>
          </Grid>
        )}
        <InputGroup
          inputAddOnText="Money Order / Cheque"
          type="checkbox"
          hasError={Boolean(emptyErrorMessage)}
          checked={chequeSelected}
          onChange={() => handleChangePaymentMethod('cheque', chequeSelected)}
        />
        {chequeSelected && (
          <Grid className={elMb4}>
            <ColSplit>
              <InputGroup
                label="Payable to *"
                type="text"
                {...register('cheque.payableTo')}
                errorMessage={errors.cheque?.payableTo?.message}
                maxLength={200}
              />
            </ColSplit>
          </Grid>
        )}
        <InputGroup
          inputAddOnText="Cash"
          type="checkbox"
          hasError={Boolean(emptyErrorMessage)}
          checked={cashSelected}
          onChange={() => handleChangePaymentMethod('cash', cashSelected)}
        />
        {cashSelected && (
          <Grid className={elMb4}>
            <ColSplit>
              <InputGroup
                label="Place of Payment *"
                type="text"
                {...register('cash.placeOfPayment')}
                errorMessage={errors.cash?.placeOfPayment?.message}
                maxLength={200}
              />
            </ColSplit>
          </Grid>
        )}
        <InputGroup
          inputAddOnText="Other"
          type="checkbox"
          hasError={Boolean(emptyErrorMessage)}
          checked={otherSelected}
          onChange={() => handleChangePaymentMethod('other', otherSelected)}
        />
        {otherSelected && (
          <Grid className={elMb4}>
            <ColSplit>
              <InputGroup
                label="Type *"
                type="text"
                {...register('other.type')}
                errorMessage={(errors.other?.type as GlobalError)?.message}
                maxLength={200}
              />
            </ColSplit>
          </Grid>
        )}
        {emptyErrorMessage && <InputError message={emptyErrorMessage} />}
      </FlexContainer>
      <ButtonGroup alignment="right" className={elMt8}>
        <Button intent="neutral" onClick={onClose} disabled={isSubmitLoading}>
          Cancel
        </Button>
        <Button intent="primary" loading={isSubmitLoading} disabled={isSubmitLoading}>
          Save
        </Button>
      </ButtonGroup>
    </form>
  )
}

export default PaymentMethodsEditForm
